/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DestroyRef, Injectable} from '@angular/core';
import {finalize, firstValueFrom} from 'rxjs';
import {NetworkDataApiService} from '../admin/network/network-data-api.service';
import {MarketplaceProductApiService} from '../admin/marketplace-product/marketplace-product-api.service';
import {CreativeApiService} from '../creative/creative-api.service';
import {resourceOptionParams} from '../common/controller/signal-admin-resource-api';
import _ from 'lodash';
import {advertiserNavigationParams} from '../../components/util/navigation-context';
import {isBidTooHigh, lineItemCommons, officialHighImpactSimple, officialProductSimple, productCreativeTag, simpleMarketplaceProducts, setLineItemDefaults, officialPoliticalProduct, officialHighImpactThirdParty, creativeTypeOptions} from './line-item-commons';
import {postFillGenerateFormatFees} from '../creative/creative-commons';
import {ADN_TARGETING_TYPES, getTargetingType} from '../targeting/targeting-constants';
import {TargetingHelper} from '../targeting/targeting-helper';
import {MoneyHelper} from '../../components/util/money';
import {getCountryName} from '../common/directives/country-select/countries';
import {SubmitCalls} from '../common/directives/submit-calls';
import {IAB_TAXONOMY} from '../../components/util/iab-taxonomy';
import {calculateNetCpm} from './fees-calculator';
import {getMatchingFee, simplifyFee} from './line-item-fee-utils';
import {
  calculateMaximumCosts,
  CreativeFormatFee,
  deriveProductCpmBounds,
  shouldApplyFixedProductBids,
  summarizeCreativeFormatFees
} from './line-item-edit-calculations';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {ogTranslate} from '../common/filters/og-translate';
import {CpaDefaults} from './cpa-defaults';
import {defaultCurrencyDeriver} from './currency-default-calculator';
import {isIabCategoryEnabledForNetwork} from './iab-category-network';
import {updateBudgets} from '../order/order-budgets';
import {buildElectionFormOptions, buildElectionSelectGroups, sameElectionId} from '../../components/util/election-form-options';
import {
  advertiserNeedsPoliticalData,
  buildTopicsFromPoliticalBudgets,
  linkedPoliticalCampaignRefs,
  loadPoliticalCampaignTopicsByElection
} from '../../components/util/political-campaign-advertiser-topics';
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {readViewSignal, writeViewSignal} from '../common/util/template-state';
import {attachLineItemEditPhase} from './line-item-edit-phase';
import type {LineItemEditPhaseApi} from './line-item-edit-phase';
import {prepareLineItemForSubmit} from './line-item-submit';
import {
  applyPersistedLineItemDeliveryState,
  applyPersistedLineItemImmediateState
} from './line-item-save-state';
import {CustomEventApiService} from '../admin/custom-event/custom-event-api.service';
import {PublicPoliticalCampaignApiService} from '../political-campaign/public-political-campaign-api.service';
import {TeamApiService} from '../admin/team/team-api.service';
import {LineItemEditApi, LineItemModel} from './line-item-api.service';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {BurnRatesApiService} from './burn-rates-api.service';
import {DataViewApiService} from '../common/controller/data-view-api.service';
import {StatsApiService} from '../common/controller/stats-api.service';
import type {LineItemEditPageComponent} from './line-item-edit-page.component';

export interface LineItemEditSetupParams {
  model: any;
  readOnly: boolean;
  full: boolean;
  viewType: number;
  adnInactiveTargets: any;
  LocalNetworkProfile: any;
  location: any;
  lineItemApi: LineItemEditApi;
  Advertiser: any;
  publicPoliticalCampaignApi: PublicPoliticalCampaignApiService;
  localUserProfile: any;
  onDestroy: (cleanup: () => void) => void;
  eventBus: any;
  burnRatesApi: BurnRatesApiService;
  NetworkData: NetworkDataApiService;
  adnLog: any;
  orderFromParam: any;
  advertiserFromParam: any;
  adnModalLoading: any;
  pageState: SignalAdminResourcePageState<LineItemModel>;
  listBinding: SignalResourceListBindingService;
  listLifetime: DestroyRef;
  teamApi: TeamApiService;
  adnStatFilter: any;
  document: any;
  Rollbar: any;
  Order: any;
  MarketplaceProduct: MarketplaceProductApiService;
  LocalUserPermissions: any;
  lineItemView: any;
  statsApi: StatsApiService;
  CustomEvent: CustomEventApiService;
  dataViewApi: DataViewApiService;
  appLocale: any;
  translate: any;
  Creative: CreativeApiService;
  adnTimeShifter: any;
  TargetingEditorModalService: any;
  $state: any;
  lineItemCpmFeesModalService: any;
  creativeFromLineItemCopyModalService: any;
  mpProductModalService: any;
  orderBudgetModalService: any;
  creativePreviewModalService: any;
  lineItemRetailProductModalService: any;
  creativeBulkActionService: any;
  creativeCopyInBulkModalService: any;
  localNetworkInfo: LocalNetworkInfo;
}

export function persistLineItemBudgetFields(
  lineItemApi: LineItemEditApi,
  modelObject: any,
  fields: string[]
): Promise<any> {
  return firstValueFrom(lineItemApi.savePartial(modelObject, fields));
}

export function applyPersistedLineItemBudget(editingModel: any, resourceModel: any, savedModel: any): void {
  const savedFields = _.pick(savedModel, [
    'budget',
    'budgetRollover',
    'validationWarnings',
    'startDate',
    'endDate',
    'objectives'
  ]);
  _.assign(editingModel, _.cloneDeep(savedFields));
  _.assign(resourceModel, _.cloneDeep(savedFields));
}

export function applyLineItemDimensionCheck(ctrl: any, data: any): void {
  // The API returns 200 with no body when there are no recently requested
  // inventory dimensions. That is a completed check with no matches.
  ctrl.unmatchedCreatives = _.keyBy(data?.unmatchedCreatives || [], 'id');
  ctrl.checksDone = true;

  _.forEach(data?.checks || [], function(check) {
    ctrl.availableFixed = ctrl.availableFixed.concat(check.unmatchedFixed).concat(check.matchedFixed);
    ctrl.availableRanges = ctrl.availableRanges.concat(check.unmatchedRanges).concat(check.matchedRanges);
  });

  ctrl.availableFixed = _.uniqWith(ctrl.availableFixed, _.isEqual);
  ctrl.availableRanges = _.uniqWith(ctrl.availableRanges, _.isEqual);
}

@Injectable({providedIn: 'root'})
export class LineItemEditBehaviorService {
  initialize(page: LineItemEditPageComponent, params: LineItemEditSetupParams): void {
    configureLineItemEdit(page, params);
  }
}

function configureLineItemEdit(ctrl: LineItemEditPageComponent, p: LineItemEditSetupParams): void {
  const {
    model, readOnly, full, viewType: viewTypeParam, adnInactiveTargets,
    LocalNetworkProfile, location, lineItemApi, Advertiser, publicPoliticalCampaignApi,
    localUserProfile, onDestroy, eventBus, burnRatesApi,
    NetworkData, adnLog, orderFromParam, advertiserFromParam,
    adnModalLoading, pageState, listBinding, teamApi, adnStatFilter,
    document, Rollbar, Order, MarketplaceProduct, LocalUserPermissions, lineItemView: lineItemViewParam,
    statsApi, CustomEvent, dataViewApi, appLocale, translate,
    Creative, adnTimeShifter, TargetingEditorModalService, $state, lineItemCpmFeesModalService,
    creativeFromLineItemCopyModalService, mpProductModalService, orderBudgetModalService,
    creativePreviewModalService, lineItemRetailProductModalService, creativeBulkActionService,
    creativeCopyInBulkModalService, localNetworkInfo
  } = p;
  let lineItemView = lineItemViewParam;
  let viewType = viewTypeParam;

  ctrl.editsMade = () => ctrl.markExternalFormDirty();
  ctrl.updateModelHappened = () => ctrl.publishModelUpdate();
  ctrl.updateRelData = function(relData, type) {
    pageState.updateRelatedData(type, relData);
  };

  const editPhase: LineItemEditPhaseApi = attachLineItemEditPhase(ctrl);
  const MARKETPLACE_GATE = 'marketplace';

  const commons = lineItemCommons(
    ctrl,
    translate,
    appLocale,
    LocalNetworkProfile,
    LocalUserPermissions,
    adnTimeShifter,
    localNetworkInfo,
    'editingModel'
  );
    const reviewWorkflowTypes = ['PROPOSED', 'CREATIVE_ONLY'];
    const creativeContentLockedWorkflowTypes = ['PROPOSED', 'CREATIVE_ONLY'];

    ctrl.isLiTemplate = model.resourceName === 'lineItemTemplate';
    ctrl.perms = LocalUserPermissions.getAllVisPermissions();
    ctrl.skips = [];
    ctrl.marketplaceProducts = [];
    writeViewSignal(ctrl, 'marketplaceResolved', false);
    writeViewSignal(ctrl, 'teams', []);
    ctrl.targetingResult = {data: false};
    ctrl.data = {clicks: 0, impressions: 0, renderedImpressions: 0, cost: 0, viewableImpressions: 0, visibleImpressions: 0, hasStats: false};
    ctrl.mistakenBidEnabled = true;
    ctrl.isPureMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
    ctrl.conversionType = 'ADVERTISER';
    viewType = LocalNetworkProfile.isCalt() && LocalUserPermissions.canUseCaltLineItemViewType2() ? 2 : 1;
    ctrl.viewType = viewType;
    ctrl.allowedDeliveryCurves = ['FAST_AS_POSSIBLE', 'EVEN', 'CAPPED', 'FRONT_LOADED', 'CATCH_UP', 'GREEDY'];
    ctrl.creativeTypeOptions = creativeTypeOptions;

    if (viewType === 2 && !model.order && !ctrl.isLiTemplate && !full) {
      $state.go('app.adv.line-items.line-item', {id: model.id, viewType: viewType, full: true}, {inherit: false});
    }

    if (ctrl.isNew && orderFromParam) {
      const order = _.cloneDeep(orderFromParam);
      if (ctrl.editingModel) {
        ctrl.editingModel.order = order;
        setTimeout(function() {
          // doing a timeout here because want to make sure everything has initiated
          ctrl.orderSelect.onSelect();
        }, 101);
      } else {
        ctrl._pendingOrderFromParam = order;
      }
    }
    if (ctrl.isNew && advertiserFromParam) {
      const advertiser = _.cloneDeep(advertiserFromParam);
      if (ctrl.editingModel) {
        ctrl.editingModel.advertiser = advertiser;
      } else {
        ctrl._pendingAdvertiserFromParam = advertiser;
      }
    }

    const originalCpm = _.get(model, ['bidSpecification', 'cpm']);
    const originalCpc = _.get(model, ['bidSpecification', 'cpc']);
    const originalCpa = _.get(model, ['bidSpecification', 'cpa', 'cost']);

    ctrl.isSpecialUser = location.hostname === 'localhost' || localUserProfile.get("username") === "broker1@adnuntius.com" || localUserProfile.get("username") === "adnuntius@adnuntius.com";
    const axPublisherPermission = model.permission === 'AX_PUBLISHER';
    ctrl.isAxPublisher = axPublisherPermission;
    ctrl.dynamicBiddingAllowed = LocalNetworkProfile.isMarketplacePlatform();
    ctrl.isMarketplace = LocalNetworkProfile.isMarketplacePlatform();
    // We only show the option to bid on "attention" in the C-ALT network
    ctrl.showAttentionBiddingGoal = LocalNetworkProfile.getNetworkTag() === 'b7462' || LocalNetworkProfile.getNetworkTag() === '107727';
    ctrl.userStateReview = 'APPROVED';
    ctrl.endPoint = axPublisherPermission ? 'approve' : 'save';
    commons.setSelfService();
    commons.setAxAdvertiser();
    ctrl.isSelfServiceOwn = LocalUserPermissions.isSelfServiceOwn();
    ctrl.allowsSecondPrice = false;
    ctrl.allowsDealId = (!ctrl.isAxPublisher && !ctrl.isAxAdvertiser) && (location.hostname === 'localhost' || _.includes(['b7462', '145b90', '1287', 'f', 'dev_network', '140f2f', '1a9cda', 'lbrm', '495f1f'], LocalNetworkProfile.getNetworkTag()));
    ctrl.showIabCategory = LocalNetworkProfile.getDefaults().iabCategoryOnLineItem === 'SHOW';
    ctrl.showIabCategoryOnNetwork = isIabCategoryEnabledForNetwork(LocalNetworkProfile.getNetworkTag());
    ctrl.currencies = LocalNetworkProfile.get('availableCurrencies');
    ctrl.primaryNetworkCurrency = LocalNetworkProfile.get('defaultCurrency');
    ctrl.canLimitBurnRates = localUserProfile.canLimitBurnRates();
    ctrl.productSimples = [{id: 1, name: 'Programmatic', description: 'Deliver your ads programmatically with any of our publishers'}, {id: 2, axId: productCreativeTag, name: 'Hosted', description: 'Host your ads with us and have them deliver elsewhere'}, {id: 3, axId: officialPoliticalProduct, name: 'Political Advertising', description: 'Delivery your political ads in accordance with European law on any of our publishers.'}, {id: 4, name: 'Custom', description: 'Choose a specialised delivery option'}];
    ctrl.advancedMode = (location.hostname === 'localhost' || localUserProfile.get("username") === "adnuntius@adnuntius.com");
    ctrl.showBudgetPeriods = LocalNetworkProfile.isPureMarketplacePlatform() || LocalNetworkProfile.getNetworkTag() === '1287';
    ctrl.hasPoliticalCapability = LocalNetworkProfile.hasPoliticalCapability();
    ctrl.elections = [];
    ctrl.electionSelectGroups = { useGroups: false, dated: [], undated: [], groups: [] };
    // Seed before first CD — *ngIf="ignoreProduct" / checkbox ngModel flip
    // undefined→true (or non-boolean→true) mid-afterInit is NG0100.
    if (ctrl.editingModel && !Object.prototype.hasOwnProperty.call(ctrl.editingModel, 'ignoreProduct')) {
      ctrl.editingModel.ignoreProduct = !!LocalNetworkProfile.getDefaults()['defaultIgnoreMarketplaceTargeting'];
    } else if (ctrl.editingModel && !_.isBoolean(ctrl.editingModel.ignoreProduct)) {
      ctrl.editingModel.ignoreProduct = !!ctrl.editingModel.ignoreProduct;
    }
    writeViewSignal(ctrl, 'publisherApprovals', []);
    writeViewSignal(ctrl, 'publisherApprovalsLoaded', false);
    writeViewSignal(ctrl, 'publisherApprovalsLoading', false);
    writeViewSignal(ctrl, 'publisherApprovalsError', false);
    writeViewSignal(ctrl, 'showPublisherApprovals', false);

    function renameProduct(team) {
      const siteCountries = _.get(team, ['defaultTargeting', ADN_TARGETING_TYPES.siteCountry.targets, ADN_TARGETING_TYPES.siteCountry.apiProp]);
      if (_.isArray(siteCountries) && siteCountries.length === 1 && siteCountries[0] === 'DK') {
        if (_.get(ctrl.editingModel, ['axProduct', 'id']) === 'pzlwjm3nmddkr3c3') {
          _.set(ctrl.editingModel, ['axProduct', 'name'], 'Publisher Platform - Danmark');
        }
      }
    }

    function updateAvailableSmoothingOptions() {
      ctrl.smoothingOptions = [];
      ctrl.allSmoothingOptions = ['FAST_AS_POSSIBLE', 'EVEN', 'FRONT_LOADED', 'CAPPED', 'CATCH_UP', 'GREEDY'];
      const assessCurves = ctrl.showEverything  || ctrl.isLiTemplate || readViewSignal(ctrl, 'marketplaceResolved') || _.get(ctrl, ['editingModel', 'team', 'type']) === 'SUI';
      let allowedDeliveryCurves = _.cloneDeep(ctrl.allowedDeliveryCurves);
      if (!allowedDeliveryCurves) {
        allowedDeliveryCurves = ['EVEN', 'FRONT_LOADED', 'CAPPED'];
      }
      if (assessCurves) {
        if (ctrl.editingModel.deliveryCurve === 'FAST_AS_POSSIBLE' || allowedDeliveryCurves.includes('FAST_AS_POSSIBLE')) {
          ctrl.smoothingOptions.push('FAST_AS_POSSIBLE');
        }
        if (ctrl.editingModel.deliveryCurve === 'EVEN' || allowedDeliveryCurves.includes('EVEN')) {
          ctrl.smoothingOptions.push('EVEN');
        }
        if (ctrl.editingModel.deliveryCurve === 'FRONT_LOADED' || allowedDeliveryCurves.includes('FRONT_LOADED')) {
          ctrl.smoothingOptions.push('FRONT_LOADED');
        }
        if (ctrl.editingModel.deliveryCurve === 'CAPPED' || allowedDeliveryCurves.includes('CAPPED')) {
          ctrl.smoothingOptions.push('CAPPED');
        }
        if (ctrl.editingModel.deliveryCurve === 'CATCH_UP' || allowedDeliveryCurves.includes('CATCH_UP')) {
          ctrl.smoothingOptions.push('CATCH_UP');
        }
        if (ctrl.editingModel.deliveryCurve === 'GREEDY' || allowedDeliveryCurves.includes('GREEDY')) {
          ctrl.smoothingOptions.push('GREEDY');
        }
      }
    }

    function defaultShowObjective() {
      writeViewSignal(ctrl, 'showObjectives', !axPublisherPermission || ctrl.editingModel.objectives);
    }

    ctrl.rateLimitTypes = ['IMPRESSION', 'RENDERED_IMPRESSION', 'VISIBLE_IMPRESSION', 'VIEWABLE_IMPRESSION', 'CLICK', 'BUDGET'];
    let mpQuery;

    function workOutMinMaxCpm(theProduct) {
      const bounds = deriveProductCpmBounds(ctrl.uiFees.fees, theProduct, ctrl.primaryNetworkCurrency);
      ctrl.editingModel.minimumCpm = bounds.minimumCpm;
      ctrl.editingModel.maximumCpm = bounds.maximumCpm;
    }

    function syncSelectedCountriesFromTargeting() {
      const countriesFromTargeting = _.get(ctrl, ['editingModel', 'targeting', ADN_TARGETING_TYPES.siteCountry.targets, ADN_TARGETING_TYPES.siteCountry.apiProp]);
      if (!_.isArray(countriesFromTargeting) || countriesFromTargeting.length === 0) {
        if (_.isUndefined(ctrl.selectedCountries)) {
          ctrl.selectedCountries = [];
        }
        return;
      }
      if (!ctrl.selectedCountries || ctrl.selectedCountries.length === 0) {
        ctrl.selectedCountries = _.cloneDeep(countriesFromTargeting);
      }
    }

    function updateProductSimpleSelection() {
      syncSelectedCountriesFromTargeting();
      const productId = _.get(ctrl, ['editingModel', 'axProduct', 'id']);
      ctrl.selectedDelivery = productId === productCreativeTag ? 2 : (productId === officialProductSimple || productId === officialHighImpactSimple || productId === officialHighImpactThirdParty) ? 1 : (productId === officialPoliticalProduct) ? 3 : 4;
      const highImpactSelected = officialHighImpactSimple === productId;
      const highImpactThirdPartySelected = officialHighImpactThirdParty === productId;
      ctrl.selectedCreativeType = highImpactSelected ? officialHighImpactSimple : highImpactThirdPartySelected ? officialHighImpactThirdParty : officialProductSimple;
      if (ctrl.selectedDelivery === 2) {
        ctrl.productString = "Hosted";
        ctrl.showSimpleOptions = true;
        return;
      }
      if (ctrl.selectedDelivery === 3) {
        ctrl.productString = "Political";
        ctrl.showSimpleOptions = true;
        return;
      }
      if (simpleMarketplaceProducts.indexOf(productId) > -1) {
        ctrl.productString = highImpactSelected ? "High Impact " : highImpactThirdPartySelected ? "Third-Party High Impact " : "";
        const countryInfo = _.map(ctrl.selectedCountries, function(country) {
          return getCountryName(country);
        }).join(", ");
        ctrl.productString += "Programmatic";
        if (countryInfo) {
          ctrl.productString += " for " + countryInfo;
        }
      } else {
        ctrl.productString = _.get(ctrl, ['editingModel', 'axProduct', 'name']);
      }

      const hasAtLeastOne = _.find(ctrl.marketplaceProducts, function(p) {
        return simpleMarketplaceProducts.indexOf(p.id) > -1;
      });
      ctrl.showSimpleOptions = !!hasAtLeastOne;
    }
    function updateProductFromSimpleSelection() {
      if (ctrl.selectedDelivery === 2) {
        ctrl.editingModel.axProduct = {id: productCreativeTag};
        return;
      }
      if (ctrl.selectedDelivery === 3) {
        ctrl.editingModel.axProduct = {id: officialPoliticalProduct};
        ensureElectionModel();
        return;
      }
      if (ctrl.selectedDelivery === 4) {
        return;
      }
      if (ctrl.selectedDelivery === 1 && ctrl.selectedCreativeType) {
        const match = _.find(ctrl.creativeTypeOptions, function (p) {
          return p.id === ctrl.selectedCreativeType;
        });
        if (match) {
          ctrl.editingModel.axProduct = {id: match.id};
          return;
        }
      }
      if (ctrl.selectedDelivery === 1) {
        const officialProductAvailable = _.find(ctrl.marketplaceProducts, function(p) {
          return p.id === officialProductSimple;
        });

        if (officialProductAvailable) {
          ctrl.editingModel.axProduct = {id: officialProductSimple};
        }
      }
    }

    function phaseSignal(key, value) {
      editPhase.viewWrite(function() {
        writeViewSignal(ctrl, key, value);
      });
    }

    function setShowBidStrategy(selProduct) {
      if (!selProduct) {
        return;
      }
      const showBidStrategy = _.get(ctrl.editingModel, ['bidSpecification', 'bidStrategy']) || _.includes(['ALLOW_VCPM', 'ALLOW_DYNAMIC', 'ALLOW_ALL'], selProduct.bidStrategyOption);
      const showVCpmStrategy = _.includes(['ALLOW_VCPM', 'ALLOW_ALL'], selProduct.bidStrategyOption);
      const showDynamicCpmStrategy = _.includes(['ALLOW_DYNAMIC', 'ALLOW_ALL'], selProduct.bidStrategyOption);
      /*
           PROD@domain=#select name, external_id from domain.team where name like '%ublicis%';
                          name                |         external_id
           -----------------------------------+-----------------------------
            Publicis Communications Norway AS | try_opt\x1F6jhbjb6bwc3hdvn0
            Publicis                          | try_opt\x1Fc1xr1zt7g75qzrbv
            Publicisgroupe                    | 126m\x1F778l8kjh8b01b168
            Publicis Media Sweden AB          | try_opt\x1Fh1257q6kx86z52z2
            ICE Norge                         | xccx0lmg3p7y0s8p
           (4 rows)
           'kdl3z9jzxkvs5h00' === WAL Media A/S
       */
      const showFloorBidStrategy = _.includes(['6jhbjb6bwc3hdvn0', 'c1xr1zt7g75qzrbv', 'h1257q6kx86z52z2', 'xccx0lmg3p7y0s8p', 'kdl3z9jzxkvs5h00'], _.get(ctrl, ['editingModel', 'team', 'id']));

      // Pre-ready: dependent sections are behind the phase spinner. Post-ready: viewWrite defers.
      phaseSignal('showBidStrategy', showBidStrategy);
      phaseSignal('showVCpmStrategy', showVCpmStrategy);
      phaseSignal('showDynamicCpmStrategy', showDynamicCpmStrategy);
      phaseSignal('showFloorBidStrategy', showFloorBidStrategy);

      if (ctrl.isNew) {
        if (showDynamicCpmStrategy) {
          ctrl.editingModel.bidSpecification.bidStrategy = 'DYNAMIC';
        } else {
          ctrl.editingModel.bidSpecification.bidStrategy = null;
        }
      }
    }

    function setRateLimitReadOnly(value) {
      phaseSignal('rateLimitReadOnly', value);
    }

    const workOutDefaultCurrency = defaultCurrencyDeriver(ctrl, localUserProfile, function(fn) {
      editPhase.viewWrite(fn);
    });
    // Seed before first CD so [defaultCurrency] is not undefined→NOK mid-check.
    ctrl.moneyCurrency = localUserProfile.getPreferredCurrency() || ctrl.primaryNetworkCurrency;
    ctrl.chartCurrency = ctrl.moneyCurrency;
    workOutDefaultCurrency(
      _.get(ctrl, ['editingModel', 'advertiser']),
      _.get(ctrl, ['editingModel', 'team']),
      _.get(ctrl, ['editingModel', 'axProduct'])
    );

    function setAdvertiserBudgets(budgets) {
      // Pre-ready: dependent sections are behind editPhaseQuery spinner. Post-ready: defer.
      editPhase.viewWrite(function() {
        ctrl.advertiserBudgets = budgets;
        ctrl.advertiserBudgetsResolved = true;
      });
    }

    function applyAdvertiserBudgets(advertiser) {
      const linkedCampaigns = linkedPoliticalCampaignRefs(advertiser);
      if (linkedCampaigns.length > 0) {
        ctrl.advertiserBudgetsResolved = false;
        loadPoliticalCampaignTopicsByElection(linkedCampaigns, publicPoliticalCampaignApi).then(function(campaignTopics) {
          setAdvertiserBudgets(campaignTopics);
        });
        return;
      }

      setAdvertiserBudgets(buildTopicsFromPoliticalBudgets(advertiser));
    }

    function loadUpAdvertiserForBudgets() {
      firstValueFrom(Advertiser.get(ctrl.editingModel.advertiser.id)).then(function(advertiser) {
        ctrl.editingModel.advertiser = advertiser;
        applyAdvertiserBudgets(advertiser);
      });
    }

    function loadAdvertiserBudgets() {
      const advertiser = ctrl.editingModel.advertiser;
      if (!advertiser) {
        setAdvertiserBudgets(null);
        return;
      }

      if (advertiserNeedsPoliticalData(advertiser)) {
        loadUpAdvertiserForBudgets();
        return;
      }

      applyAdvertiserBudgets(advertiser);
    }

    function isPoliticalLineItem() {
      return _.get(ctrl.editingModel, ['axProduct', 'type']) === 'POLITICAL' || _.get(ctrl.editingModel, ['axProduct', 'id']) === officialPoliticalProduct || !!_.get(ctrl.editingModel, ['election', 'id']);
    }

    function ensureElectionModel() {
      if (!ctrl.editingModel || ctrl.isLiTemplate) {
        return;
      }
      const showElection = _.get(ctrl.editingModel, ['axProduct', 'type']) === 'POLITICAL' ||
        ctrl.hasPoliticalCapability ||
        !!_.get(ctrl.editingModel, ['election', 'id']);
      if (!showElection) {
        return;
      }
      const election = ctrl.editingModel.election;
      const electionId = _.isObject(election) ? _.get(election, 'id') : (_.isString(election) ? election : null);
      // Select empty option is [ngValue]="null". Keep "no election" as null — not
      // {} or {id:null} — otherwise compareById misses and the select writes null
      // during verify (NG0100 '{}'→'null').
      if (!electionId) {
        ctrl.editingModel.election = null;
        return;
      }
      if (!_.isObject(election) || _.get(election, 'id') !== electionId) {
        ctrl.editingModel.election = {id: electionId};
      }
    }

    function canLoadPublisherApprovals() {
      return !ctrl.isNew && !ctrl.isLiTemplate && ctrl.isMarketplace && isPoliticalLineItem();
    }

    function resetPublisherApprovals() {
      writeViewSignal(ctrl, 'publisherApprovals', []);
      ctrl.publisherApprovalsExpanded = false;
      writeViewSignal(ctrl, 'publisherApprovalsLoaded', false);
      writeViewSignal(ctrl, 'publisherApprovalsLoading', false);
      writeViewSignal(ctrl, 'publisherApprovalsError', false);
    }

    ctrl.loadPublisherApprovals = function(force) {
      const shouldShow = canLoadPublisherApprovals();
      if (!shouldShow) {
        phaseSignal('showPublisherApprovals', false);
        setTimeout(function() { resetPublisherApprovals(); }, 0);
        return;
      }
      if (!ctrl.editingModel.id || readViewSignal(ctrl, 'publisherApprovalsLoading') || (readViewSignal(ctrl, 'publisherApprovalsLoaded') && !force)) {
        if (!readViewSignal(ctrl, 'showPublisherApprovals')) {
          phaseSignal('showPublisherApprovals', true);
        }
        return;
      }

      // Guard against overlapping fetches before the deferred show flag applies.
      writeViewSignal(ctrl, 'publisherApprovalsLoading', true);
      writeViewSignal(ctrl, 'publisherApprovalsError', false);
      phaseSignal('showPublisherApprovals', true);
      phaseSignal('publisherApprovalsLoading', true);
      firstValueFrom(lineItemApi.getPublisherApprovals(ctrl.editingModel.id)).then(function(response) {
        // AdnHttp already settles past the zoneless tick — sync signal writes are fine.
        writeViewSignal(ctrl, 'publisherApprovals', _.isArray(response) ? response : (_.get(response, 'results') || _.get(response, 'approvals') || []));
        writeViewSignal(ctrl, 'publisherApprovalsLoaded', true);
      }, function() {
        writeViewSignal(ctrl, 'publisherApprovalsError', true);
      }).finally(function() {
        writeViewSignal(ctrl, 'publisherApprovalsLoading', false);
      });
    };

    ctrl.getPublisherApprovalLabel = function(approval) {
      if (approval === 'APPROVED') {
        return 'Approved';
      }
      if (approval === 'BLOCKED') {
        return 'Blocked';
      }
      if (approval === 'PENDING') {
        return 'Pending';
      }
      return approval || 'Unknown';
    };

    ctrl.getPublisherApprovalClass = function(approval) {
      if (approval === 'APPROVED') {
        return 'label-success';
      }
      if (approval === 'BLOCKED') {
        return 'label-danger';
      }
      return 'label-default';
    };

    ctrl.getPublisherApprovalScopeLabel = function(approval) {
      if (_.has(approval, 'creative')) {
        return 'Creative';
      }
      if (_.has(approval, 'lineItem')) {
        return 'Line Item';
      }
      if (_.has(approval, 'order')) {
        return 'Order';
      }
      if (_.has(approval, 'advertiser')) {
        return 'Advertiser';
      }
      return 'Line Item';
    };

    ctrl.getPublisherApprovalStatusLabel = function(approval) {
      const approvalStatus = _.get(approval, 'approval');
      const label = ctrl.getPublisherApprovalLabel(approvalStatus);
      if (approvalStatus === 'PENDING') {
        return label;
      } else {
        return label + ' ' + ctrl.getPublisherApprovalScopeLabel(approval);
      }
    };

    ctrl.getPublisherApprovalScopeObject = function(approval) {
      if (_.has(approval, 'creative')) {
        return _.get(approval, 'creative') || {};
      }
      if (_.has(approval, 'lineItem')) {
        return _.get(approval, 'lineItem') || {};
      }
      if (_.has(approval, 'order')) {
        return _.get(approval, 'order') || {};
      }
      if (_.has(approval, 'advertiser')) {
        return _.get(approval, 'advertiser') || {};
      }
      return {};
    };

    ctrl.getPublisherApprovalScopeName = function(approval) {
      const scopeObject = ctrl.getPublisherApprovalScopeObject(approval);
      return _.get(scopeObject, 'name') || _.get(scopeObject, 'id') || '';
    };

    function setMarketplaceResolved(value) {
      // Pre-ready writes are sync (spinner hides dependent sections). Gate completion
      // enters ready once; later toggles use viewWrite.
      editPhase.viewWrite(function() {
        writeViewSignal(ctrl, 'marketplaceResolved', value);
      });
      if (value) {
        editPhase.done(MARKETPLACE_GATE);
        setTimeout(function() {
          updateAvailableSmoothingOptions();
        }, 0);
      }
    }

    /**
     * Apply marketplace product fields sync, but defer the axProduct object swap.
     * adn-promise-spinner buffers marketplaceResolved=false, so the product name
     * stays on-screen during hydrate — a sync name change NG0100s (e.g. HESA→DIAS).
     */
    function hydrateAxProductFromMarketplace(pendingAxProduct) {
      const axProductId = _.get(ctrl.editingModel, ['axProduct', 'id']) || _.get(pendingAxProduct, 'id');
      if (!axProductId) {
        updateProductSimpleSelection();
        return;
      }
      const foundProduct = _.find(ctrl.marketplaceProducts, function(mp) {
        return mp.id === axProductId;
      });
      if (!foundProduct) {
        adnLog.error("Line item's product is not currently available to the line item.");
        if (pendingAxProduct) {
          editPhase.viewWrite(function() {
            ctrl.editingModel.axProduct = pendingAxProduct;
            updateProductSimpleSelection();
          });
          return;
        }
        updateProductSimpleSelection();
        return;
      }

      ctrl.showIabCategory = ctrl.showIabCategory && foundProduct.type !== 'CREATIVE_TAG';
      getUiFees();
      workOutMinMaxCpm(foundProduct);
      workOutDefaultCurrency(_.get(ctrl, ['editingModel', 'advertiser']), _.get(ctrl, ['editingModel', 'team']), foundProduct);
      ctrl.editingModel.bidSpecification = ctrl.editingModel.bidSpecification || {};
      setShowBidStrategy(foundProduct);
      ctrl.allowedDeliveryCurves = _.get(foundProduct, 'smoothingChoices') || [];
      if (ctrl.allowedDeliveryCurves.length === 0) {
        ctrl.allowedDeliveryCurves = null;
      }
      if (foundProduct.cpmSpecification === 'FIXED') {
        ctrl.editingModel.bidSpecification.cpm = foundProduct.minimumCpm;
      }
      commons.setBidSpecificationFields(foundProduct);

      if (ctrl.isNew) {
        if (foundProduct.cpmSpecification === 'FIXED') {
          ctrl.editingModel.bidSpecification.cpm = foundProduct.minimumCpm;
        }
        if (_.includes(['ALLOW_DYNAMIC', 'ALLOW_ALL'], foundProduct.bidStrategyOption) && _.get(ctrl, ['editingModel', 'type']) !== 'CREATIVE_TAG') {
          ctrl.editingModel.bidSpecification.bidStrategy = 'DYNAMIC';
        }
      }
      editPhase.viewWrite(function() {
        ctrl.editingModel.axProduct = foundProduct;
        renameProduct(ctrl.editingModel.team);
        updateProductSimpleSelection();
        // minCpmForSave flips false→true here on a team/product re-hydrate that
        // can land post-ready — same NG0100 risk as the save-success flip below.
        if (ctrl.isNew && foundProduct.cpmSpecification === 'FIXED') {
          ctrl.minCpmForSave = true;
        }
      });
    }

    function marketplaceProductQuery(resetQuery?: boolean, teamId?: any) {
      editPhase.wait(MARKETPLACE_GATE);
      // Re-read each call — auth/network switch may refresh LocalNetworkInfo after setup.
      const axProductsCount = localNetworkInfo.getInfo("axProductsCount");
      if (axProductsCount < 1) {
        setMarketplaceResolved(true);
        return;
      }
      if (ctrl.editingModel.axProduct || _.get(ctrl.editingModel, ['team', 'type']) === 'AX_ADVERTISER' || teamId) {
        let teamToProduct;
        let defaultsQuery;
        ctrl.showIabCategory = ctrl.editingModel.type !== 'CREATIVE_TAG';
        if (ctrl.isNew && !ctrl.editingModel.axProduct) {
          if (!defaultsQuery) {
            defaultsQuery = firstValueFrom(NetworkData.listOptions(resourceOptionParams({filterByValue: 'defaultMarketplaceProducts', filterBy: 'type'})).pipe(finalize(() => editPhase.viewWrite(() => {})))).then(function(page) {
              if (_.get(page, ['results', 'length'])) {
                const entry = page.results[0];
                try {
                  const mapping = entry.data.teamToProduct;
                  teamToProduct = _.isString(mapping) ? JSON.parse(mapping) : mapping;
                } catch {
                  adnLog.warn("Found a problem find teams to products for defaults");
                }
              }
            });
          }
        }
        if (mpQuery && !resetQuery) {
          getUiFees();
          setMarketplaceResolved(true);
          return;
        }
        setMarketplaceResolved(false);
        mpQuery = firstValueFrom(MarketplaceProduct.listOptions(resourceOptionParams({excludeInvalid: true, team: _.get(ctrl, ['editingModel', 'team', 'id'], teamId)})).pipe(finalize(() => editPhase.viewWrite(() => {})))).then(function(page) {
          ctrl.marketplaceProducts = page.results || [];

          const hasCreativeTagProduct = _.find(ctrl.marketplaceProducts, function(mp) {
            return mp.id === productCreativeTag;
          });
          if (!hasCreativeTagProduct) {
            ctrl.productSimples = _.filter(ctrl.productSimples, function(p) {
              return p.axId !== productCreativeTag;
            });
          }

          const hasPoliticalProduct = _.find(ctrl.marketplaceProducts, function(mp) {
            return mp.id === officialPoliticalProduct;
          });
          if (!hasPoliticalProduct) {
            ctrl.productSimples = _.filter(ctrl.productSimples, function(p) {
              return p.axId !== officialPoliticalProduct;
            });
          }

          // Return the inner promise so finally / Promise.resolve(mpQuery) wait for
          // product field writes (cpm, currency, delivery curve, etc.).
          return Promise.resolve(defaultsQuery).then(function() {
            const currentTeamId = _.get(ctrl.editingModel, ['team', 'id']) || teamId;
            let pendingAxProduct = null;
            if (teamToProduct) {
              const allMatchedMps = _.filter(teamToProduct, function(val, key) {
                const isTeam = LocalUserPermissions.has('AX_ADVERTISER', key) || LocalUserPermissions.has('MANAGE_ADVERTISING', key);
                return (currentTeamId === key || !currentTeamId) && isTeam && _.find(ctrl.marketplaceProducts, function(mp) {
                  return mp.id === teamToProduct[key];
                });
              });
              const matchedMpId = allMatchedMps.length === 1 ? allMatchedMps[0] : null;
              if (matchedMpId) {
                const matchedMp = _.find(ctrl.marketplaceProducts, ['id', matchedMpId]);
                if (matchedMp) {
                  _.set(ctrl.editingModel, 'targeting', _.cloneDeep(matchedMp.targeting));
                  ctrl.editingModel.rateLimits = matchedMp.rateLimits;
                  setRateLimitReadOnly(ctrl.editingModel.rateLimits.length > 0);
                  setShowBidStrategy(matchedMp);
                  pendingAxProduct = matchedMp;
                }
              }
            }
            hydrateAxProductFromMarketplace(pendingAxProduct);
          });
        }).finally(function() {
          setMarketplaceResolved(true);
          updateAvailableSmoothingOptions();
          ctrl.loadPublisherApprovals();
        });
      } else {
        setMarketplaceResolved(true);
        ctrl.loadPublisherApprovals();
      }
    }

    function updateTargetingOnDefaults() {
      if (!ctrl.isNew) {
        return;
      }
      const currentTargeting = TargetingHelper.getTargetsWithTargetsDirectly(ctrl.editingModel.targeting);
      const defaultTargeting = _.cloneDeep(_.get(ctrl.editingModel, ['team', 'defaultTargeting']));
      if (_.isEmpty(currentTargeting) && defaultTargeting) {
        const dts = TargetingHelper.getTargetsWithTargetsDirectly(defaultTargeting);
        if (!_.isEmpty(dts)) {
          _.forEach(dts, function(dt) {
            ctrl.editingModel.targeting[dt.targets] = _.cloneDeep(defaultTargeting[dt.targets]);
          });
          // New object reference so adn-target-summary ngOnChanges rebuilds snapshots.
          ctrl.editingModel.targeting = _.cloneDeep(ctrl.editingModel.targeting);
          syncSelectedCountriesFromTargeting();
        }
      }
    }

    ctrl.iabTaxonomy = _.cloneDeep(IAB_TAXONOMY);
    ctrl.orderSelect = {
      onSelect: function() {
        const orderId = _.get(ctrl.editingModel, ['order', 'id']);
        if (!orderId) {
          return;
        }
        setMarketplaceResolved(false);
        Order.get(orderId).subscribe(function(order) {
          const teamId = _.get(order, ['team', 'id']);
          const currentTeamId = _.get(ctrl.editingModel, ['team', 'id']);
          const advertiserId = _.get(order, ['advertiser', 'id']);
          if (advertiserId) {
            Advertiser.get(advertiserId).subscribe(function(advertiser) {
              ctrl.editingModel.advertiser = advertiser;
              loadAdvertiserBudgets();
              ctrl.publishModelUpdate?.();
            });
          }
          if (teamId) {
            marketplaceProductQuery(true, teamId);
            if (currentTeamId !== teamId) {
              teamApi.get(teamId).subscribe((team) => {
                ctrl.editingModel.team = team;

                updateTargetingOnDefaults();

                ctrl.isSui = _.get(ctrl.editingModel, ['team', 'type']) === 'SUI';
                getUiFees();
                workOutMinMaxCpm(ctrl.editingModel.axProduct);
                workOutDefaultCurrency(_.get(ctrl, ['editingModel', 'advertiser']), _.get(ctrl, ['editingModel', 'team']), ctrl.editingModel.axProduct);
                renameProduct(ctrl.editingModel.team);
              });
            }
          } else {
            ctrl.editingModel.team = null;
            marketplaceProductQuery(true, teamId);
          }
        }, function() {
          setMarketplaceResolved(true);
        });
      }
    };

    ctrl.bulkAction = creativeBulkActionService.createBulkAction(ctrl);

    ctrl.copyInBulk = function() {
      creativeCopyInBulkModalService.open(ctrl, Creative, function() {
        ctrl.makeQuery();
      });
    };

    const calcCostFunc = function() {
      const costs = calculateMaximumCosts(ctrl.editingModel, ctrl.data, LocalNetworkProfile.getDefaultCpmCostModel());
      ctrl.costFailsafe = false;
      if (ctrl.mistakenBidEnabled) {
        const cpm = _.get(ctrl.editingModel, ['bidSpecification', 'cpm'], {amount: 0, currency: 'AUD'});
        const cpc = _.get(ctrl.editingModel, ['bidSpecification', 'cpc'], {amount: 0, currency: 'AUD'});
        const cpa = _.get(ctrl.editingModel, ['bidSpecification', 'cpa', 'cost'], {amount: 0, currency: 'AUD'});
        const cpmBidTooHigh = isBidTooHigh(cpm, originalCpm);
        const cpcBidTooHigh = isBidTooHigh(cpc, originalCpc);
        const cpaBidTooHigh = isBidTooHigh(cpa, originalCpa);
        if (cpmBidTooHigh || cpcBidTooHigh || cpaBidTooHigh) {
          ctrl.costFailsafe = cpmBidTooHigh || cpcBidTooHigh || cpaBidTooHigh;
        }
      }
      ctrl.maxCpmCost = costs.maxCpmCost;
      ctrl.maxCpcCost = costs.maxCpcCost;
      getUiFees();
    };

    ctrl.confirmBid = function() {
      ctrl.mistakenBidEnabled = false;
      ctrl.costFailsafe = false;
    };

    function doDimChecks() {
      if (ctrl.isLiTemplate) {
        return;
      }

      ctrl.checksDone = false;
      ctrl.unmatchedCreatives = [];
      ctrl.availableFixed = [];
      ctrl.availableRanges = [];
      if (ctrl.editingModel.type !== 'CREATIVE_TAG' && !ctrl.isNew) {
        const targetingCount = TargetingHelper.getSizeOfTargetingTypes(ctrl.editingModel.targeting, ADN_TARGETING_TYPES.publisher.targets) +
          TargetingHelper.getSizeOfTargetingTypes(ctrl.editingModel.targeting, ADN_TARGETING_TYPES.site.targets) +
          TargetingHelper.getSizeOfTargetingTypes(ctrl.editingModel.targeting, ADN_TARGETING_TYPES.adUnit.targets);

        if (targetingCount > 0) {
          firstValueFrom(lineItemApi.dimensionsCheck(ctrl.editingModel.id)).then(function(data) {
            editPhase.viewWrite(function() {
              applyLineItemDimensionCheck(ctrl, data);
            });
          });
        }
      }
    }

    const formatFeesCache: CreativeFormatFee[] = [];
    let numCreativeCache = -1;

    ctrl.canLimitBurnRates = localUserProfile.canLimitBurnRates();
    const burnRateModel = ctrl.editingModel || model;
    burnRateModel.burnRateLimits = burnRateModel.burnRateLimits || [];
    if (burnRateModel.burnRateLimits.length === 1) {
      ctrl.burnRateLimitsType = 'SINGLE';
    } else if (burnRateModel.burnRateLimits.length === 4 && _.filter(burnRateModel.burnRateLimits, function(bl) {return bl > 0;}).length > 0) {
      ctrl.burnRateLimitsType = 'VARIABLE';
    } else {
      burnRateModel.burnRateLimits = [0.0, 0.0, 0.0, 0.0];
      ctrl.burnRateLimitsType = 'NONE';
    }

    function getUiFees(creativeFormatFees?: any[], numCreatives = -1) {
      const grossCpm = _.cloneDeep(_.get(ctrl, ['editingModel', 'bidSpecification', 'cpm']) || _.get(model, ['bidSpecification', 'cpm']));
      const grossCpc = _.cloneDeep(_.get(ctrl, ['editingModel', 'bidSpecification', 'cpc']) || _.get(model, ['bidSpecification', 'cpc']));
      const referenceCurrency = _.get(grossCpm, ['currency']) || _.get(grossCpc, ['currency']) || _.get(ctrl, ['editingModel', 'billingCurrency']) || 'NA';
      ctrl.uiFees = ctrl.uiFees || {fees: []};
      ctrl.uiFees.fees.length = 0;

      _.forEach(_.cloneDeep(_.get(ctrl, ['editingModel', 'bidSpecification', 'cpmFees'])), function(fee) {
        ctrl.uiFees.fees.push(fee);
      });
      _.forEach(_.cloneDeep(_.get(ctrl, ['editingModel', 'bidSpecification', 'audienceFees'])), function(fee) {
        if (fee.name === 'NORDIC_DATA_RESOURCES') {
          fee.name = "Audience Fee for Nordic Data Resources";
        } else {
          const targetType = getTargetingType(fee.name);
          if (targetType) {
            fee.name = "Fee for " + targetType.title + " targeting";
          } else {
            fee.name = "Audience Fee for Third-Party Audience";
          }
        }
        fee.id = 'audience-fee'; // needed to make the name read-only
        ctrl.uiFees.fees.push(fee);
      });
      _.forEach(_.cloneDeep(_.get(ctrl, ['editingModel', 'bidSpecification', 'supplementaryFees'])), function(fee) {
        fee.id = 'supplementary-fee'; // needed to make the name read-only
        ctrl.uiFees.fees.push(fee);
      });
      if (_.get(ctrl.editingModel, ['bidSpecification', 'cpmTechFee', 'type'])) {
        const cpmTechFee = _.cloneDeep(ctrl.editingModel.bidSpecification.cpmTechFee);
        cpmTechFee.name = LocalNetworkProfile.getNetworkTranslations("techFee");
        cpmTechFee.id = "tech-fee";
        ctrl.uiFees.fees.push(cpmTechFee);
      }
      if (creativeFormatFees) {
        numCreativeCache = numCreatives;
        formatFeesCache.length = 0;
        _.forEach(creativeFormatFees, function(f) {
          const fee = simplifyFee(LocalNetworkProfile, referenceCurrency, f);
          if (fee) {
            formatFeesCache.push(fee);
          }
        });
      }
      if (formatFeesCache.length) {
        const ff = summarizeCreativeFormatFees(
          formatFeesCache,
          numCreativeCache,
          referenceCurrency,
          LocalNetworkProfile.getNetworkTranslations("creativeFormatFee")
        );
        if (ff) {
          ctrl.uiFees.fees.push(ff);
        }
      }

      function standardiseFees(fees) {
        _.forEach(fees, function(theFee) {
          if (theFee.type === 'MULTI_CURRENCY_MONETARY') {
            const matchingFee = getMatchingFee(theFee.multiCurrencyMonetaryAmounts, referenceCurrency);
            if (matchingFee) {
              theFee.monetary = matchingFee;
            }
          }
        });
      }

      standardiseFees(ctrl.uiFees.fees);
      standardiseFees(creativeFormatFees);
      ctrl.uiFees.grossCpm = MoneyHelper.fixMoney(grossCpm);
      ctrl.uiFees.netCpm = calculateNetCpm(ctrl.uiFees.fees, MoneyHelper.fixMoney(grossCpm));
      return referenceCurrency;
    }

    ctrl.selectTeams = function() {
      ctrl.editsMade();
      ctrl.editingModel.teams = _.map(readViewSignal<any[]>(ctrl, 'teams'), 'id');
    };

    ctrl.deselectTeams = function() {
      ctrl.editsMade();
      ctrl.editingModel.teams = [];
    };

    teamApi.listOptions().subscribe((page) => {
      const allTeams = page.results;
      const isViewAllObjects = LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
      writeViewSignal(ctrl, 'teams', (isViewAllObjects ? allTeams : _.filter(allTeams, function(g) {
        return LocalUserPermissions.isPartOf(g.network.tagId, g.id);
      })) || []);
    });

    ctrl.ignoreProductChange = function() {
      ctrl.showEverything = !LocalNetworkProfile.isPureMarketplacePlatform() || ctrl.editingModel.ignoreProduct;
    };

    const afterInitAfterSave = function() {
      if (!Object.prototype.hasOwnProperty.call(ctrl.editingModel, 'ignoreProduct')) {
        ctrl.editingModel.ignoreProduct = !!LocalNetworkProfile.getDefaults()['defaultIgnoreMarketplaceTargeting'];
      } else if (!_.isBoolean(ctrl.editingModel.ignoreProduct)) {
        ctrl.editingModel.ignoreProduct = !!ctrl.editingModel.ignoreProduct;
      }
      ctrl.editingModel.biddingAlgorithm = ctrl.editingModel.biddingAlgorithm || 'DEFAULT';
      if (_.get(ctrl.editingModel, ['bidSpecification', 'optimiseFor', 'customEventType', 'id'])) {
        ctrl.editingModel.bidSpecification.optimiseFor.eventType = 'CUSTOM';
      }

      const selectedElectionId = _.get(ctrl.editingModel, ['election', 'id']);
      ctrl.elections = buildElectionFormOptions(localNetworkInfo.getSuppInfo("elections") || [], {
        username: localUserProfile.get('username'),
        canSeeTestElections: localUserProfile.canSeeTestElections(),
        selectedElectionId: selectedElectionId,
        onlyFuture: true
      });
      ctrl.electionSelectGroups = buildElectionSelectGroups(ctrl.elections);

      const selectedElection = _.find(ctrl.elections, function(election) {
        return sameElectionId(election.id, selectedElectionId);
      });
      if (selectedElection) {
        ctrl.editingModel.election = selectedElection;
      }
      ensureElectionModel();

      function ifSelProductFound(doFunc) {
        Promise.resolve(mpQuery).then(function() {
          if (!ctrl.editingModel.axProduct) {
            return;
          }

          const selProduct = _.find(ctrl.marketplaceProducts, function(mp) {
            return mp.id === ctrl.editingModel.axProduct.id;
          });
          if (selProduct) {
            // Pre-ready: spinner hides the form. Post-ready: viewWrite defers.
            editPhase.viewWrite(function() {
              doFunc(selProduct);
              workOutMinMaxCpm(selProduct);
              ctrl.allowedDeliveryCurves = _.get(ctrl.editingModel.axProduct, 'smoothingChoices') || [];
              if (ctrl.allowedDeliveryCurves.length === 0) {
                ctrl.allowedDeliveryCurves = null;
              }
              const curves = ctrl.allowedDeliveryCurves === null ? ['EVEN', 'FRONT_LOADED', 'CAPPED'] : ctrl.allowedDeliveryCurves;
              if (!ctrl.editingModel.deliveryCurve) {
                ctrl.editingModel.deliveryCurve = curves[0];
              }
              updateAvailableSmoothingOptions();
            });
          }
        });
      }

      getUiFees();
      doDimChecks();
      ctrl.editingModel.teams = _.map(ctrl.editingModel.teams, function(team) {
        return _.get(team, ['id'], team);
      });

      if (ctrl.isLiTemplate) {
        ctrl.editingModel.teamType = _.get(ctrl.editingModel, 'order') || _.get(ctrl.editingModel, 'teams', []).length === 0 ? 'ORDER' : 'TEAMS';
      }

      ctrl.showEverything = ctrl.showEverything === true || !LocalNetworkProfile.isPureMarketplacePlatform() || _.get(ctrl.editingModel, ['team', 'type']) === 'STANDARD';
      ctrl.showProposed = ctrl.showProposed || ctrl.showEverything || ((ctrl.editingModel.userState === 'PROPOSED' || ctrl.editingModel.userState === 'APPROVED') && (ctrl.editingModel.executionState === 'NOT_READY' || ctrl.editingModel.executionState === 'READY'));
      ctrl.showRetailProducts = localNetworkInfo.hasProductFeedProducts();
      ctrl.hasShowEverythingOption = LocalNetworkProfile.isPureMarketplacePlatform() && ctrl.isAdOps;
      updateAvailableSmoothingOptions();

      // Defer — isLockedDown / singleBudgetObjective / showObjectives flip *ngIf
      // true→false after first paint (save settle + product hydrate) (NG0100).
      phaseSignal('isLockedDown', ctrl.isAxPublisher);
      setTimeout(function() {
        // setObjectiveFields also runs during setup, but showEverything is not known yet —
        // without this, basic line items only get marketplace (budget-only) objective fields.
        commons.setObjectiveFields();
        commons.setBidSpecificationFields();
      }, 0);

      const skipValidationWarning = 'validation.warning.lineItem.unapproved';
      if (!ctrl.isAxAdvertiser) {
        ifSelProductFound(function(selProduct) {
          commons.setObjectiveFields(selProduct);
          // doing this to get the expanded info
          ctrl.editingModel.axProduct = selProduct;
          setShowBidStrategy(selProduct);
        });

        return;
      }

      ifSelProductFound(function(selProduct) {
        // doing this to get the expanded info
        ctrl.editingModel.axProduct = selProduct;
        commons.setObjectiveFields(selProduct);
        setShowBidStrategy(selProduct);
        ctrl.requiresReview = reviewWorkflowTypes.indexOf(selProduct.lineItemWorkflowType) > -1;
        if (ctrl.requiresReview && !model.axProduct) {
          ctrl.editingModel.userState = 'PROPOSED';
        }
        ctrl.showIabCategory = ctrl.showIabCategory && selProduct.type !== 'CREATIVE_TAG';
        phaseSignal('isLockedDown', ctrl.editingModel.userState !== 'PROPOSED' && selProduct.lineItemWorkflowType === 'PROPOSED');
        ctrl.isCreativeContentLocked = ctrl.editingModel.userState !== 'PROPOSED' && creativeContentLockedWorkflowTypes.indexOf(selProduct.lineItemWorkflowType) > -1;
        ctrl.submitForReview = ctrl.editingModel.userState === 'PROPOSED' && ctrl.requiresReview;
        ctrl.canSubmitForReview = _.get(ctrl.editingModel, ['validationWarnings', 'length']) === 0 || (_.get(ctrl.editingModel, ['validationWarnings', 'length']) === 1 && _.get(ctrl.editingModel.validationWarnings, [0, 'code']) === skipValidationWarning);
        ctrl.skips = ctrl.requiresReview && (ctrl.editingModel.userState === 'PROPOSED' || ctrl.editingModel.userState === 'SUBMITTED' || ctrl.editingModel.userState === 'RESERVED') ? [skipValidationWarning] : [];
        ctrl.canStop = ctrl.editingModel.userState === 'APPROVED' && ctrl.requiresReview;
        setRateLimitReadOnly(_.get(ctrl.editingModel.axProduct, ['rateLimits', 'length']) > 0);
        if (selProduct.type === "CREATIVE_TAG") {
          ctrl.editingModel.type = 'CREATIVE_TAG';
          phaseSignal('showObjectives', (readViewSignal(ctrl, 'showObjectives') && selProduct.objectiveSpecification !== 'OPTIONAL') || ctrl.editingModel.objectives);
        } else {
          ctrl.editingModel.type = 'AUCTION';
          defaultShowObjective();
        }
        if (selProduct.bidStrategyOption === 'SPONSORSHIP') {
          ctrl.editingModel.type = 'SPONSORSHIP';
        }
        if (ctrl.isNew) {
          ctrl.editingModel.bidSpecification.bidStrategy = selProduct.bidStrategyOption === 'ALLOW_VCPM' ? 'VIEWABLE' : null;
        }
        setShowBidStrategy(selProduct);
        const currentTargets = TargetingHelper.getTargetsWithTargets(ctrl.editingModel, ['targeting']);
        const optionalTargeting = _.get(ctrl.editingModel, ['axProduct', 'optionalTargetTypes'], []);
        ctrl.hideTargeting = ctrl.isAxAdvertiser && (!ctrl.editingModel.axProduct || (_.isEmpty(currentTargets) && optionalTargeting.length === 0));
      });
    };

    ctrl.data = {clicks: 0, impressions: 0, renderedImpressions: 0, cost: 0, viewableImpressions: 0, visibleImpressions: 0, hasStats: false};
    setLineItemDefaults(LocalNetworkProfile, ctrl, ctrl.editingModel || model);
    if (!ctrl.isNew && !ctrl.isLiTemplate && !axPublisherPermission) {
      statsApi.get({
        lineItemId: model.id,
        timeout: 30000,
        groupBy: null
      }).subscribe({next: function(apiData) {
        const clicks = apiData.clicks;
        const impressions = apiData.impressions;
        const cost = apiData.cost;
        const viewableImpressions = apiData.viewables;
        const visibleImpressions = apiData.visibles;
        const renderedImpressions = apiData.rendered;
        // Defer past the current CD turn. Stats settle can still race the edit
        // form's checkNoChanges (Delivered *ngIf="!data.hasStats"), and calcCost
        // mutates form pending state in the same callback.
        setTimeout(function() {
          ctrl.data.clicks = clicks;
          ctrl.data.impressions = impressions;
          ctrl.data.cost = cost;
          ctrl.data.viewableImpressions = viewableImpressions;
          ctrl.data.visibleImpressions = visibleImpressions;
          ctrl.data.renderedImpressions = renderedImpressions;
          ctrl.data.hasStats = true;
          writeViewSignal(ctrl, 'hasStats', true);
          calcCostFunc();
        });
      }, error: _.noop});

      if (model.smoothed && model.executionState !== 'NOT_READY') {
        firstValueFrom(burnRatesApi.getForLineItem(model.id)).then(function(response) {
          writeViewSignal(ctrl, 'currentBurnRate', response.currentBurnRate);
        }).catch(_.noop);
      }
    }

    const creativeQuery = function() {
      if (_.isFunction(ctrl.makeQuery)) {
        return;
      }
      // want to minimise layout parameter information, which can be heavy duty
      listBinding.create(Creative, 'fullCreativesForLineItem', p.listLifetime, {
      baseTable: 'creative',
        moreParams: {lineItem: model.id},
        concise: true,
        pageSize: 101,
        afterFill: function() {
          ctrl.updateRelData(ctrl.items, 'creatives');

          const data = postFillGenerateFormatFees(ctrl);
          const refCurrency = getUiFees(data.creativeFormatFees, data.validItems.length);
          _.forEach(ctrl.items, function(item) {
            const fee = simplifyFee(LocalNetworkProfile, refCurrency, item.creativeFormatFee);
            if (fee) {
              item.simplifiedCreativeFormatFee = fee;
            }
          });
        }
      }, ctrl);
    };

    function modLineItemDates() {
      if (ctrl.editingModel.startDate) {
        ctrl.editingModel.modStartDate = ctrl.editingModel.startDate;
      }
      if (ctrl.editingModel.endDate) {
        ctrl.editingModel.modEndDate = ctrl.editingModel.endDate;
      }
    }

    ctrl._lineItemAfterSave = function(persistedLineItem) {
      ctrl.targetingResult = ctrl.targetingResult || {};
      ctrl.targetingResult.data = false;
      applyPersistedLineItemImmediateState(ctrl.editingModel, model, persistedLineItem);
      // Delivery-label fields are applied deferred — sync wipe/assign of
      // executionStateEndedReason (undefined→null) NG0100s form_1_div_*_div_3.
      // minCpmForSave flips true→false here too — same NG0100 risk on the
      // CPM/min-CPM banner *ngIf, so it rides along in the same deferred write.
      editPhase.viewWrite(function() {
        applyPersistedLineItemDeliveryState(ctrl.editingModel, model, persistedLineItem);
        ctrl.minCpmForSave = false;
      });

      if (!ctrl.isLiTemplate) {
        creativeQuery();

        firstValueFrom(dataViewApi.getView('LineItemView', {id: persistedLineItem.id}, false)).then(function(liv) {
          lineItemView = liv;
          ctrl.editingModel.advertiser = _.get(lineItemView, 'advertiser');
          model.advertiser = _.get(lineItemView, 'advertiser');
          workOutDefaultCurrency(_.get(ctrl, ['editingModel', 'advertiser']), _.get(ctrl, ['editingModel', 'team']), ctrl.editingModel.axProduct);
          ctrl.updateModelHappened();
        });
      }

      afterInitAfterSave();
      updateBudgets(ctrl, persistedLineItem);
      writeViewSignal(ctrl, 'publisherApprovalsLoaded', false);
      ctrl.loadPublisherApprovals(true);

      ctrl.currentEndDate = ctrl.editingModel.endDate;
      ctrl.currentSmoothed = ctrl.editingModel.smoothed;

      if (full && viewType === 2) {
        $state.go('app.adv.line-items.line-item', {id: persistedLineItem.id, viewType: 2, full: false}, {inherit: false});
      }
      pageState.setPanelFull(false);
      adnInactiveTargets.cancelExistingData(model.id);
    };

    ctrl._lineItemAfterInit = function() {
      if (ctrl._pendingOrderFromParam) {
        ctrl.editingModel.order = ctrl._pendingOrderFromParam;
        delete ctrl._pendingOrderFromParam;
        setTimeout(function() {
          ctrl.orderSelect.onSelect();
        }, 101);
      }
      if (ctrl._pendingAdvertiserFromParam) {
        ctrl.editingModel.advertiser = ctrl._pendingAdvertiserFromParam;
        delete ctrl._pendingAdvertiserFromParam;
      }

      if (!ctrl.isNew && !ctrl.isLiTemplate) {
        creativeQuery();
      }
      modLineItemDates();

      // for an AX_PUBLISHER viewing a line item, some parts of the line item will be suppressed
      ctrl.readOnlyAxPublisher = axPublisherPermission && !ctrl.editingModel.order;
      ctrl.showTargeting = !axPublisherPermission || ctrl.editingModel.targeting;
      ctrl.showOrder = !axPublisherPermission || _.get(ctrl.editingModel, ['order', 'name']);
      defaultShowObjective();
      ctrl.showBidSpecification = !axPublisherPermission || ctrl.editingModel.bidSpecification;
      ctrl.showExclusionLabels = !axPublisherPermission || ctrl.editingModel.exclusionLabels;
      ctrl.showLabels = !axPublisherPermission || ctrl.editingModel.labels;
      ctrl.blockSubmit = readOnly || ctrl.blockSubmit || ctrl.readOnlyAxPublisher;

      if (ctrl.isSelfService && !ctrl.editingModel.order) {
        Order.listOptions({minimal: true}).subscribe(function(page) {
          ctrl.editingModel.order = _.get(_.head(page.results), 'id') || {};
        });
      }
      ctrl.currentOrder = _.get(lineItemView, 'order') || ctrl.currentOrder;

      ctrl.showOrderVelocity = (typeof(ctrl.editingModel.orderVelocity) !== 'undefined') && (typeof(ctrl.editingModel.orderDeliveryPercentage) !== 'undefined');
      updateBudgets(ctrl, ctrl.currentOrder);
      ctrl.editingModel.advertiser = _.get(lineItemView, 'advertiser') || ctrl.editingModel.advertiser;
      loadAdvertiserBudgets();

      ctrl.editingModel.team = _.get(lineItemView, 'team');
      ctrl.isSui = _.get(ctrl.editingModel, ['team', 'type']) === 'SUI';
      marketplaceProductQuery();
      afterInitAfterSave();
      updateBudgets(ctrl);
      calcCostFunc();
      renameProduct(ctrl.editingModel.team);
      ctrl.editingModel.bidSpecification = ctrl.editingModel.bidSpecification || {};
      ctrl.editingModel.bidSpecification.cpa = ctrl.editingModel.bidSpecification.cpa || {};
      ctrl.editingModel.bidSpecification.cpa.cost = ctrl.editingModel.bidSpecification.cpa.cost || {};
      ctrl.editingModel.bidSpecification.cpa.initialECpm = ctrl.editingModel.bidSpecification.cpa.initialECpm || {};
      ctrl.editingModel.objectives = ctrl.editingModel.objectives || {};
      ctrl.editingModel.bidSpecification.cpmCostModel = ctrl.editingModel.bidSpecification.cpmCostModel || null;
      ctrl.editingModel.bidSpecification.bidStrategy = ctrl.editingModel.bidSpecification.bidStrategy || null;
      // Re-seed on cancel/reset (ensureSetup is one-shot) — Delivery label NG0100.
      if (ctrl.editingModel.executionStateEndedReason === undefined) {
        ctrl.editingModel.executionStateEndedReason = null;
      }
      if (!ctrl.editingModel.axProduct) {
        phaseSignal('showVCpmStrategy', true);
        phaseSignal('showDynamicCpmStrategy', true);
      }
      ctrl.axUserStates = {};
      if (ctrl.isAxAdvertiser) {
        ctrl.axUserStates = {
          PROPOSED: ctrl.editingModel.userState === 'PROPOSED',
          SUBMITTED: ctrl.editingModel.userState === 'SUBMITTED',
          RESERVED: ctrl.editingModel.userState === 'RESERVED'
        };
      }

      if (ctrl.isNew) {
        ctrl.editingModel.deliveryCurve = ctrl.isAxAdvertiser ? LocalNetworkProfile.getDefaults().marketplaceDeliveryCurve : LocalNetworkProfile.getDefaults().deliveryCurve;
      }

      ctrl.currentEndDate = ctrl.editingModel.endDate;
      ctrl.currentSmoothed = ctrl.editingModel.smoothed;
      ctrl.loadPublisherApprovals();
    };

    ctrl._lineItemAfterInitAfterSave = afterInitAfterSave;
    ctrl._ensureElectionModel = ensureElectionModel;

    if (ctrl.editingModel) {
      ctrl.editingModel.targeting = ctrl.editingModel.targeting || {};
      ctrl.editingModel.optimiseCreativesFor = ctrl.editingModel.optimiseCreativesFor || null;
      ctrl.editingModel.bidSpecification = ctrl.editingModel.bidSpecification || {};
      ctrl.editingModel.bidSpecification.optimiseFor = ctrl.editingModel.bidSpecification.optimiseFor || {eventType: 'IMPRESSION'};
      // Seed before first CD so Delivery [executionStateEndedReason] and btnRadio
      // null options are not undefined→null mid-check (NG0100).
      if (ctrl.editingModel.executionStateEndedReason === undefined) {
        ctrl.editingModel.executionStateEndedReason = null;
      }
      ctrl.editingModel.bidSpecification.cpmCostModel = ctrl.editingModel.bidSpecification.cpmCostModel || null;
      ctrl.editingModel.bidSpecification.bidStrategy = ctrl.editingModel.bidSpecification.bidStrategy || null;
      if (_.get(ctrl.editingModel, ['bidSpecification', 'optimiseFor', 'eventType']) === null) {
        _.set(ctrl.editingModel, ['bidSpecification', 'optimiseFor', 'eventType'], 'IMPRESSION');
      }
      ensureElectionModel();
    }

    commons.setObjectiveFields();
    commons.setBidSpecificationFields();

    ctrl.modelObjectiveDeliveryCss = {
      default: 'label-default',
      UNAVAILABLE: 'label-warning',
      DELIVER: 'label-success',
      UNDER_DELIVER: 'label-danger',
      UNCERTAIN: 'label-warning'
    };
    ctrl.calcCost = calcCostFunc;

    ctrl.showCpa = _.isFinite(_.get(ctrl.editingModel.bidSpecification, 'cpa.cost.amount'));
    ctrl.setCpaLink = function() {
      ctrl.cpaLinkLineItem = CpaDefaults.getLink(_.get(ctrl.cpaCustomEvent, ['id']), LocalNetworkProfile.get('tagId'), _.get(ctrl.editingModel, 'id'));
      ctrl.cpaLinkAdvertiser = CpaDefaults.getLink(_.get(ctrl.cpaCustomEvent, ['id']), LocalNetworkProfile.get('tagId'), _.get(ctrl.editingModel, ['advertiser', 'id']));
      ctrl.cpaScriptLineItem = CpaDefaults.getScript(_.get(ctrl.cpaCustomEvent, ['id']), LocalNetworkProfile.get('tagId'), _.get(ctrl.editingModel, 'id'));
      ctrl.cpaScriptAdvertiser = CpaDefaults.getScript(_.get(ctrl.cpaCustomEvent, ['id']), LocalNetworkProfile.get('tagId'), _.get(ctrl.editingModel, ['advertiser', 'id']));
    };
    ctrl.setCpaLink();

    CustomEvent.list({
      includeHidden: false,
      includeActive: true,
      includeInactive: false,
      orderBy: 'name',
      orderByDirection: 'ASCENDING',
      page: 1,
      pageSize: 1001,
      concise: false
    }).subscribe((page) => writeViewSignal(ctrl, 'customEvents', page.results));

    commons.setUpTiers();

    commons.setObjectivesAsString();
    ctrl.objectivesAsString = ctrl.editingModelObjectivesAsString;

    ctrl.retailProductCreative = function() {
      lineItemRetailProductModalService.open(_.get(ctrl, ['editingModel', 'id']), function() {
        ctrl.makeQuery();
      });
    };

    ctrl.openCreativePreview = function(creative) {
      creativePreviewModalService.open({
        creative: creative,
        lineItem: ctrl.editingModel,
        teamId: _.get(lineItemView, 'order.team.id')
      });
    };

    ctrl.editBudgets = function() {
      orderBudgetModalService.open({
        modelObject: _.cloneDeep(ctrl.editingModel),
        moneyCurrency: _.cloneDeep(ctrl.moneyCurrency),
        objTranslation: 'Line Item',
        persistFields: function(modelObject, fields) {
          return persistLineItemBudgetFields(lineItemApi, modelObject, fields);
        }
      }).then(function(data) {
        if (data.obj) {
          applyPersistedLineItemBudget(ctrl.editingModel, model, data.obj);
          modLineItemDates();
          updateBudgets(ctrl);
        }
      });
    };

    ctrl.editTarget = function(targeting) {
      const formDirtyState = ctrl.captureFormDirtyState();
      ctrl.targetingResult = {
        data: false
      };
      const modelLineItem = _.cloneDeep(ctrl.editingModel);
      modelLineItem.creativesForReach = ctrl.items;
      const productType = _.get(ctrl.editingModel, ['axProduct', 'type']);
      const lineItemType = (productType === 'POLITICAL' || _.get(ctrl.editingModel, ['election', 'id'])) ? 'POLITICAL' : 'NORMAL';
      const mandatoryTargets = TargetingHelper.getTargetsWithTargets(ctrl.editingModel, ['axProduct', 'targeting']);
      let availableTargets;
      if (ctrl.isAxAdvertiser || !(ctrl.editingModel.ignoreProduct || !_.get(ctrl.editingModel, ['axProduct']))) {
        const currentTargets = TargetingHelper.getTargetsWithTargets(ctrl.editingModel, ['targeting']);
        const optionalTargeting = _.get(ctrl.editingModel, ['axProduct', 'optionalTargetTypes'], []);
        const optionalTargetIds = Object.values(ADN_TARGETING_TYPES).filter(function(tt) {
          return _.indexOf(optionalTargeting, tt.apiType) > -1 || mandatoryTargets[tt.id] || currentTargets[tt.id];
        }).map(tt => tt.id);
        availableTargets = _.pick(ADN_TARGETING_TYPES, optionalTargetIds);
      }

      TargetingEditorModalService.open({
        modelLineItem: modelLineItem,
        lineItemType: lineItemType,
        pageType: "lineItem",
        reachEnabled: 'LINE_ITEM',
        mandatoryTargets: mandatoryTargets,
        overridableTargeting: undefined,
        availableTargets: availableTargets,
        targetingMode: null,
        targeting: targeting,
        isLockedDown: readViewSignal(ctrl, 'isLockedDown')
      }).then(function(wc) {
        if (!wc) {
          return;
        }
        _.assign(targeting, wc.wc);
        _.set(ctrl.editingModel, 'targeting', _.cloneDeep(targeting));
        editPhase.viewWrite(() => {});
        if ((_.get(targeting, 'thirdPartyAudienceTargets') || []).length > 0) {
          // NDR is currently the only possible third-party provider. When we have others we will
          // need to check the specific audiences targeted to determine what providers must be enabled
          ctrl.editingModel.enabledThirdPartyProviders = ['NORDIC_DATA_RESOURCES'];
        } else {
          ctrl.editingModel.enabledThirdPartyProviders = [];
        }
        adnInactiveTargets.cancelExistingData(model.id);
        if (!ctrl.isNew) {
          return firstValueFrom(lineItemApi.savePartial(
            ctrl.editingModel,
            ['targeting', 'enabledThirdPartyProviders'],
            {ignoreProduct: ctrl.editingModel.ignoreProduct}
          ).pipe(finalize(() => editPhase.viewWrite(() => {})))).then(function(respLineItem) {
            ctrl.targetingResult.data = true;
            _.set(ctrl.editingModel, 'validationWarnings', respLineItem.validationWarnings);
            _.set(ctrl.editingModel, 'targeting', respLineItem.targeting);
            _.set(ctrl.editingModel, 'enabledThirdPartyProviders', respLineItem.enabledThirdPartyProviders);
            _.assign(model, _.pick(_.cloneDeep(respLineItem), [
              'validationWarnings', 'targeting', 'enabledThirdPartyProviders'
            ]));
            _.set(ctrl.editingModel, 'bidSpecification.audienceFees', _.get(respLineItem, ['bidSpecification', 'audienceFees']));
            getUiFees();
            doDimChecks();
            workOutMinMaxCpm(ctrl.editingModel.axProduct);
            editPhase.viewWrite(() => {});

            SubmitCalls.updateFormWarnings(ctrl.formSubmitState, respLineItem);
          }, function(err) {
            ctrl.targetingResult.errors = _.get(err, ['data', 'errors']);
            Rollbar.error("Line item targeting problem", err);
            editPhase.viewWrite(() => {});
          });
        }
      }).finally(function() {
        ctrl.restoreFormDirtyState(formDirtyState);
      });
    };

    const lineItemTargetingUpdateCall = eventBus.on('lineItemTargetingUpdate', function(event, respLineItem) {
      _.set(ctrl.editingModel, 'validationWarnings', respLineItem.validationWarnings);
      _.set(ctrl.editingModel, 'targeting', respLineItem.targeting);
      _.set(ctrl.editingModel, 'enabledThirdPartyProviders', respLineItem.enabledThirdPartyProviders);
      _.set(ctrl.editingModel, 'bidSpecification.audienceFees', _.get(respLineItem, ['bidSpecification', 'audienceFees']));
      getUiFees();
      workOutMinMaxCpm(ctrl.editingModel.axProduct);

      SubmitCalls.updateFormWarnings(ctrl.formSubmitState, respLineItem);
    });
    onDestroy(lineItemTargetingUpdateCall);

    ctrl.selectAxProduct = function(prod) {
      ctrl.editingModel.axProduct = prod;
      ensureElectionModel();
      updateProductSimpleSelection();
    };
    ctrl.selectCountry = function(country) {
      ctrl.selectedCountries = ctrl.selectedCountries || [];
      if (ctrl.selectedCountries.indexOf(country.id) > -1) {
        _.pull(ctrl.selectedCountries, country.id);
      } else {
        ctrl.selectedCountries.push(country.id);
      }
      updateProductSimpleSelection();
    };
    ctrl.selectCreativeType = function(ct) {
      ctrl.selectedCreativeType = ct.id;
      updateProductFromSimpleSelection();
    };
    ctrl.selectDelivery = function(delivery) {
      ctrl.selectedDelivery = delivery.id;
      updateProductFromSimpleSelection();
    };

    ctrl.selectMp = function() {
      mpProductModalService.open({
        allProducts: ctrl.marketplaceProducts,
        initialAxProduct: ctrl.editingModel.axProduct,
        isMarketplace: LocalNetworkProfile.isPureMarketplacePlatform(),
        initialActiveTab: ctrl.marketplaceProducts.length > 10 ? 'table' : 'block',
        onSave: function(axProduct, allAxProducts) {
          function rateLimitChecks(rateLimits) {
            setRateLimitReadOnly(rateLimits.length > 0);
            if (rateLimits.length > 0) {
              ctrl.editingModel.rateLimits = rateLimits;
            }
          }

          const foundHydratedProduct = _.find(allAxProducts, function(allProduct) {
            return allProduct.id === axProduct.id;
          });
          if (!ctrl.isNew) {
            const previousAxProduct = ctrl.editingModel.axProduct;
            const previousUserState = ctrl.editingModel.userState;
            ctrl.editingModel.axProduct = axProduct;
            if (reviewWorkflowTypes.indexOf(axProduct.lineItemWorkflowType) > -1 && ctrl.editingModel.executionState === 'NOT_READY') {
              ctrl.editingModel.userState = 'PROPOSED';
            }
            return firstValueFrom(lineItemApi.savePartial(
              ctrl.editingModel,
              ['axProduct', 'targeting', 'userState'],
              {ignoreProduct: ctrl.editingModel.ignoreProduct}
            ).pipe(finalize(() => editPhase.viewWrite(() => {})))).then(function(respLineItem) {
              _.set(ctrl.editingModel, 'axProduct', respLineItem.axProduct);
              _.set(ctrl.editingModel, 'targeting', respLineItem.targeting);
              _.set(ctrl.editingModel, 'validationWarnings', respLineItem.validationWarnings);
              _.assign(model, _.pick(_.cloneDeep(respLineItem), [
                'axProduct', 'targeting', 'userState', 'validationWarnings', 'bidSpecification', 'rateLimits'
              ]));
              _.set(ctrl.editingModel, ['bidSpecification', 'cpmTechFee'], _.get(respLineItem, ['bidSpecification', 'cpmTechFee']));
              _.set(ctrl.editingModel, ['bidSpecification', 'cpmFees'], _.get(respLineItem, ['bidSpecification', 'cpmFees']));
              _.set(ctrl.editingModel, ['bidSpecification', 'cpm'], _.get(respLineItem, ['bidSpecification', 'cpm']));
              _.set(ctrl.editingModel, ['bidSpecification', 'cpc'], _.get(respLineItem, ['bidSpecification', 'cpc']));

              getUiFees();
              workOutMinMaxCpm(ctrl.editingModel.axProduct);
              workOutDefaultCurrency(_.get(ctrl, ['editingModel', 'advertiser']), _.get(ctrl, ['editingModel', 'team']), _.get(ctrl, ['editingModel', 'axProduct']));
              rateLimitChecks(respLineItem.rateLimits);
              commons.setBidSpecificationFields(foundHydratedProduct);
              const warningModel = _.isFunction(ctrl.getWarningModel) ? ctrl.getWarningModel() : respLineItem;
              SubmitCalls.updateFormWarnings(ctrl.formSubmitState, warningModel, LocalNetworkProfile.isPureMarketplacePlatform());
            }).catch(function(resp) {
              ctrl.editingModel.axProduct = previousAxProduct;
              ctrl.editingModel.userState = previousUserState;
              return Promise.reject(resp);
            });
          }

          _.set(ctrl.editingModel, 'axProduct', axProduct);
          _.set(ctrl.editingModel, 'targeting', axProduct.targeting);
          if (shouldApplyFixedProductBids(foundHydratedProduct, ctrl.editingModel.bidSpecification)) {
            _.set(ctrl.editingModel, ['bidSpecification', 'cpm'], foundHydratedProduct.minimumCpm);
            _.set(ctrl.editingModel, ['bidSpecification', 'cpc'], foundHydratedProduct.minimumCpc);
          }
          rateLimitChecks(_.get(ctrl.editingModel, ['axProduct', 'rateLimits'], []));
          commons.setBidSpecificationFields(foundHydratedProduct);
          return Promise.resolve();
        },
        onSaved: function() {
          // Product selection is an explicit user edit. Preserve it if the
          // Angular form's asynchronous initialization is still settling.
          ctrl.onUserFieldInput();
          afterInitAfterSave();
        }
      });
    };

    ctrl.creativeCopy = function() {
      creativeFromLineItemCopyModalService.open({
        destinationLineItem: ctrl.editingModel,
        destinationLineItemName: ctrl.editingModel.name || 'This Line Item',
        specifiedAdvertiser: LocalNetworkProfile.isPureMarketplacePlatform() && ctrl.editingModel.advertiser
          ? ctrl.editingModel.advertiser
          : null,
        preselectCurrentLineItem: _.get(ctrl, ['items', 'length']) > 0,
        perms: ctrl.perms,
        onClosed: function() {
          ctrl.makeQuery();
        }
      });
    };

    ctrl.updateCpmPrefix = function() {
      ctrl.cpmPrefix = '';
      if (_.get(ctrl.editingModel, ['bidSpecification', 'bidStrategy']) === 'VIEWABLE') {
        let hasMonetaryFees = false;
        if (_.get(ctrl.editingModel, ['bidSpecification', 'cpmFees', 'length']) > 0) {
          hasMonetaryFees = !!_.find(ctrl.editingModel.bidSpecification.cpmFees, function(fee) {
            return fee.type === 'MONETARY';
          });
        }
        hasMonetaryFees = hasMonetaryFees || (_.get(ctrl.editingModel.bidSpecification, ['techFee', 'type']) || '') === 'MONETARY';
        if (!hasMonetaryFees) {
          ctrl.cpmPrefix = 'v';
        }
      } else if (_.get(ctrl.editingModel, ['bidSpecification', 'bidStrategy']) === 'DYNAMIC' || _.get(ctrl.editingModel, ['bidSpecification', 'bidStrategy']) === 'FLOOR') {
        ctrl.cpmPrefix = 'Max ';
      }
    };
    ctrl.updateCpmPrefix();

    ctrl.editCpmFees = function() {
      let ogFees = _.cloneDeep(ctrl.editingModel.bidSpecification);
      let hasError = false;
      lineItemCpmFeesModalService.open({
        moneyCurrency: ctrl.moneyCurrency,
        uiFees: ctrl.uiFees,
        isAdOps: ctrl.isAdOps,
        isPureMarketplace: LocalNetworkProfile.isPureMarketplacePlatform(),
        isAuction: ctrl.editingModel.type !== 'CREATIVE_TAG',
        isNew: ctrl.isNew,
        onSave: function(uiFees) {
          ctrl.editingModel.bidSpecification.cpm = _.cloneDeep(uiFees.grossCpm);
          ctrl.editingModel.bidSpecification.cpmFees = _.cloneDeep(uiFees.fees || []).filter(function(fee) {
            return fee.id !== 'tech-fee' && fee.id !== 'audience-fee' && fee.id !== 'creative-format-fee' && fee.id !== 'supplementary-fee';
          });
          ctrl.editingModel.bidSpecification.supplementaryFees = _.cloneDeep(uiFees.fees || []).filter(function(fee) {
            return fee.id === 'supplementary-fee';
          }) || [];
          ctrl.updateCpmPrefix();
          if (!ctrl.isNew) {
            adnModalLoading.applyModalLoading();
            return firstValueFrom(lineItemApi.savePartial(
              ctrl.editingModel,
              'bidSpecification',
              {ignoreProduct: ctrl.editingModel.ignoreProduct}
            ).pipe(finalize(() => editPhase.viewWrite(() => {})))).then(function(respLineItem) {
              _.set(ctrl.editingModel, 'validationWarnings', respLineItem.validationWarnings);
              _.set(ctrl.editingModel, ['bidSpecification', 'cpmTechFee'], _.get(respLineItem, ['bidSpecification', 'cpmTechFee']));
              _.set(ctrl.editingModel, ['bidSpecification', 'audienceFees'], _.get(respLineItem, ['bidSpecification', 'audienceFees']));
              _.set(ctrl.editingModel, ['bidSpecification', 'supplementaryFees'], _.get(respLineItem, ['bidSpecification', 'supplementaryFees']));
              _.set(ctrl.editingModel, ['bidSpecification', 'cpmFees'], _.get(respLineItem, ['bidSpecification', 'cpmFees']));
              _.assign(model, _.pick(_.cloneDeep(respLineItem), ['validationWarnings', 'bidSpecification']));
              ogFees = _.cloneDeep(ctrl.editingModel.bidSpecification);
              getUiFees();
              workOutMinMaxCpm(ctrl.editingModel.axProduct);
              adnModalLoading.removeModalLoading();
            }, function(err) {
              adnModalLoading.removeModalLoading();
              hasError = true;
              return Promise.reject(err);
            });
          }
          ctrl.editsMade();
          return Promise.resolve();
        }
      }).then(function(result) {
        if (!result.saved && hasError) {
          ctrl.editingModel.bidSpecification = ogFees;
        }
      });
    };

    ctrl.removeCpmFee = (cpmFee) => {
      const index = ctrl.editingModel.bidSpecification.cpmFees.indexOf(cpmFee);
      if (index !== -1) {
        ctrl.editingModel.bidSpecification.cpmFees.splice(index, 1);
        ctrl.editsMade();
      }
    };

    function submitAfterAction(userState) {
      const form = document.getElementById("lineItemForm");
      if (form) {
        ctrl.editingModel.userState = userState;
        setTimeout(function() {
          form.dispatchEvent(new Event('submit', {bubbles: true, cancelable: true}));
        }, 1);
      }
    }

    ctrl.newBulkCreative = function() {
      $state.go('app.adv.creatives.bulkupload', _.assign({},
        ctrl.editingModel.advertiser ? advertiserNavigationParams(ctrl.editingModel.advertiser) : {},
        {
          lineItemId: ctrl.editingModel.id,
          lineItemName: ctrl.editingModel.name
        }));
    };

    ctrl.submitAxForReview = function() {
      submitAfterAction('SUBMITTED');
    };

    ctrl.stopMarketplaceLineItem = function() {
      submitAfterAction('STOPPED');
    };

    ctrl.submitPublisherReview = function() {
      if (ctrl.userStateReview === 'APPROVED') {
        submitAfterAction('APPROVED');
      } else {
        submitAfterAction('PROPOSED');
      }
    };

    ctrl.cancelProductChange = function() {
      pageState.setPanelFull(false);
      ctrl.editingModel.axProduct = model.axProduct;
    };

    ctrl.onSaveAndNext = function() {
      const productId = _.get(ctrl, ['editingModel', 'axProduct', 'id']);
      _.set(ctrl.editingModel, ['targeting', ADN_TARGETING_TYPES.siteCountry.targets, ADN_TARGETING_TYPES.siteCountry.apiProp], []);
      if (productCreativeTag !== productId) {
        _.set(ctrl.editingModel, ['targeting', ADN_TARGETING_TYPES.siteCountry.targets, ADN_TARGETING_TYPES.siteCountry.apiProp], ctrl.selectedCountries);
      }
    };

    ctrl.onBeforeSubmit = function() {
      prepareLineItemForSubmit({
        editingModel: ctrl.editingModel,
        persistedModel: model,
        clientErrors: ctrl.formSubmitState.clientErrors,
        viewType: ctrl.viewType,
        isAxPublisher: axPublisherPermission,
        isLineItemTemplate: ctrl.isLiTemplate,
        burnRateLimitsType: ctrl.burnRateLimitsType,
        data: ctrl.data,
        networkTag: LocalNetworkProfile.getNetworkTag(),
        isSelfService: ctrl.isSelfService,
        isNew: ctrl.isNew,
        showCpa: ctrl.showCpa,
        modDates: () => commons.modDates(ctrl.editingModel.modStartDate, ctrl.editingModel.modEndDate),
        objectiveLabel: (translationKey) => ogTranslate(
          'report.field.' + translationKey,
          undefined,
          function(k) { return LocalNetworkProfile.getNetworkTranslations(k); },
          function(k, params) { return translate.instant(k, params); }
        ),
        formatStat: adnStatFilter,
        incrementLineItemTemplatesCount: () => localNetworkInfo.increment('lineItemTemplatesCount')
      });
    };

    ctrl._lineItemOnBeforeSubmit = ctrl.onBeforeSubmit;
  
}
